/**
 * User Types & Constants
 *
 * PENTING: Nama konstanta disamakan dengan Booku Lama (mdl_VariabelUmum.vb:116-151)
 * untuk memudahkan komparasi dan konsistensi.
 */

// ============================================
// KONSTANTA LEVEL USER
// (mdl_VariabelUmum.vb:126-145)
// ============================================

/** Level 99 - App Developer (internal) */
export const LevelUser_99_AppDeveloper = 99;

/** Level 81 - Tim IT (internal) */
export const LevelUser_81_TimIT = 81;

/** Level 9 - Super User */
export const LevelUser_09_SuperUser = 9;

/** Level 4 - General User */
export const LevelUser_04_GeneralUser = 4;

/** Level 3 - Direktur */
export const LevelUser_03_Direktur = 3;

/** Level 2 - Manager */
export const LevelUser_02_Manager = 2;

/** Level 1 - Operator */
export const LevelUser_01_Operator = 1;

// ============================================
// KONSTANTA JABATAN USER
// (mdl_VariabelUmum.vb:127-139)
// ============================================

/** Jabatan App Developer */
export const JabatanUser_AppDeveloper = 'App Developer';

/** Jabatan Tim IT */
export const JabatanUser_TimIT = 'Tim IT';

/** Jabatan Super User */
export const JabatanUser_SuperUser = 'Super User';

/** Jabatan General User */
export const JabatanUser_GeneralUser = 'General User';

/** Jabatan Direktur */
export const JabatanUser_Direktur = 'Direktur';

/** Jabatan Manager */
export const JabatanUser_Manager = 'Manager';

/** Jabatan Operator */
export const JabatanUser_Operator = 'Operator';

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface UserData {
  username: string;
  nama: string;
  jabatan: string;
  level: number;
  clusterFinance: boolean;
  clusterAccounting: boolean;
  isActive: boolean;
}

export interface UserListResponse {
  users: UserData[];
  totalCount: number;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  nama: string;
  jabatan: string;
  clusterFinance: boolean;
  clusterAccounting: boolean;
}

export interface UpdateUserRequest {
  nama?: string;
  jabatan?: string;
  clusterFinance?: boolean;
  clusterAccounting?: boolean;
}

export interface ToggleStatusRequest {
  isActive: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// ============================================
// JABATAN OPTIONS (untuk dropdown)
// ============================================

export const JABATAN_OPTIONS = [
  { value: JabatanUser_SuperUser, label: JabatanUser_SuperUser, level: LevelUser_09_SuperUser },
  { value: JabatanUser_GeneralUser, label: JabatanUser_GeneralUser, level: LevelUser_04_GeneralUser },
  { value: JabatanUser_Direktur, label: JabatanUser_Direktur, level: LevelUser_03_Direktur },
  { value: JabatanUser_Manager, label: JabatanUser_Manager, level: LevelUser_02_Manager },
  { value: JabatanUser_Operator, label: JabatanUser_Operator, level: LevelUser_01_Operator },
] as const;

// ============================================
// LEVEL LABELS (untuk display)
// ============================================

export const LEVEL_LABELS: Record<number, string> = {
  [LevelUser_01_Operator]: JabatanUser_Operator,
  [LevelUser_02_Manager]: JabatanUser_Manager,
  [LevelUser_03_Direktur]: JabatanUser_Direktur,
  [LevelUser_04_GeneralUser]: JabatanUser_GeneralUser,
  [LevelUser_09_SuperUser]: JabatanUser_SuperUser,
  [LevelUser_81_TimIT]: JabatanUser_TimIT,
  [LevelUser_99_AppDeveloper]: JabatanUser_AppDeveloper,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Mendapatkan label jabatan dari level
 */
export function getLevelLabel(level: number): string {
  return LEVEL_LABELS[level] || `Level ${level}`;
}

/**
 * Mendapatkan level dari jabatan
 */
export function getLevelFromJabatan(jabatan: string): number {
  const option = JABATAN_OPTIONS.find((j) => j.value === jabatan);
  return option?.level ?? LevelUser_01_Operator;
}

/**
 * Cek apakah level adalah SuperUser atau lebih tinggi
 */
export function isSuperUserOrAbove(level: number): boolean {
  return level >= LevelUser_09_SuperUser;
}

/**
 * Cek apakah level adalah Direktur atau lebih tinggi
 */
export function isDirekturOrAbove(level: number): boolean {
  return level >= LevelUser_03_Direktur;
}

/**
 * Cek apakah level adalah Tim IT atau lebih tinggi
 */
export function isTimITOrAbove(level: number): boolean {
  return level >= LevelUser_81_TimIT;
}

/**
 * Cek apakah level adalah App Developer
 */
export function isAppDeveloper(level: number): boolean {
  return level === LevelUser_99_AppDeveloper;
}

// ============================================
// PERMISSION HELPERS (untuk fitur dengan akses view/edit berbeda)
// ============================================

/**
 * Permission untuk Company Profile
 * - View: Direktur (3) ke atas
 * - Edit: SuperUser (9) ke atas
 */
export const CompanyProfilePermission = {
  minLevelView: LevelUser_03_Direktur,
  minLevelEdit: LevelUser_09_SuperUser,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  canEdit: (level: number) => level >= LevelUser_09_SuperUser,
  readOnlyMessage: 'Anda hanya dapat melihat data. Hubungi Super User untuk melakukan perubahan.',
};

/**
 * Permission untuk Data User
 * - View: SuperUser (9) ke atas
 * - Edit: SuperUser (9) ke atas
 */
export const DataUserPermission = {
  minLevelView: LevelUser_09_SuperUser,
  minLevelEdit: LevelUser_09_SuperUser,
  canView: (level: number) => level >= LevelUser_09_SuperUser,
  canEdit: (level: number) => level >= LevelUser_09_SuperUser,
  readOnlyMessage: 'Anda tidak memiliki akses untuk mengelola data user.',
};

/**
 * Permission untuk Data COA
 * - View: Direktur (3) ke atas
 * - Edit: SuperUser (9) ke atas
 */
export const DataCOAPermission = {
  minLevelView: LevelUser_03_Direktur,
  minLevelEdit: LevelUser_09_SuperUser,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  canEdit: (level: number) => level >= LevelUser_09_SuperUser,
  readOnlyMessage: 'Anda hanya dapat melihat data. Hubungi Super User untuk melakukan perubahan.',
};

/**
 * Permission untuk Data Lawan Transaksi (Mitra)
 * - View: Direktur (3) ke atas
 * - Edit: SuperUser (9) ke atas
 * - Delete: DISABLED (data protection)
 */
export const DataLawanTransaksiPermission = {
  minLevelView: LevelUser_03_Direktur,
  minLevelEdit: LevelUser_09_SuperUser,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  canEdit: (level: number) => level >= LevelUser_09_SuperUser,
  readOnlyMessage: 'Anda hanya dapat melihat data. Hubungi Super User untuk melakukan perubahan.',
};

/**
 * Permission untuk Data Karyawan
 * - View: Direktur (3) ke atas
 * - Edit: SuperUser (9) ke atas
 * - Delete: App Developer (99) saja
 */
export const DataKaryawanPermission = {
  minLevelView: LevelUser_03_Direktur,
  minLevelEdit: LevelUser_09_SuperUser,
  minLevelDelete: LevelUser_99_AppDeveloper,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  canEdit: (level: number) => level >= LevelUser_09_SuperUser,
  canDelete: (level: number) => level === LevelUser_99_AppDeveloper,
  readOnlyMessage: 'Anda hanya dapat melihat data. Hubungi Super User untuk melakukan perubahan.',
};

/**
 * Permission untuk Data Toko
 * - View: Direktur (3) ke atas
 * - Edit: SuperUser (9) ke atas
 * - Delete: DISABLED (data protection)
 */
export const DataTokoPermission = {
  minLevelView: LevelUser_03_Direktur,
  minLevelEdit: LevelUser_09_SuperUser,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  canEdit: (level: number) => level >= LevelUser_09_SuperUser,
  readOnlyMessage: 'Anda hanya dapat melihat data. Hubungi Super User untuk melakukan perubahan.',
};

/**
 * Permission untuk Data Project
 * - View: Direktur (3) ke atas
 * - Edit: Direktur (3) ke atas
 * - Delete: Direktur (3) ke atas
 */
export const DataProjectPermission = {
  minLevelView: LevelUser_03_Direktur,
  minLevelEdit: LevelUser_03_Direktur,
  minLevelDelete: LevelUser_03_Direktur,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  canEdit: (level: number) => level >= LevelUser_03_Direktur,
  canDelete: (level: number) => level >= LevelUser_03_Direktur,
  readOnlyMessage: 'Anda tidak memiliki akses untuk mengelola Data Project.',
};

/**
 * Permission untuk Deposit Operasional
 * - View: Direktur (3) ke atas
 * - Edit/Delete: Belum diimplementasi (read-only untuk sementara)
 *
 * Catatan:
 * - Deposit Operasional adalah advance payment dari customer
 * - Fitur CRUD akan diimplementasi nanti (PR/Roadmap)
 * - Saat ini hanya read-only untuk view daftar dan detail
 */
export const DepositOperasionalPermission = {
  minLevelView: LevelUser_03_Direktur,
  canView: (level: number) => level >= LevelUser_03_Direktur,
  readOnlyMessage: 'Fitur input, edit, dan hapus akan diimplementasi di versi berikutnya.',
};
